import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Paper, Typography, Tooltip, IconButton, Fab, withStyles } from '@material-ui/core';
import { Refresh, Add, Delete } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { MuiTableRows } from '../../../themes';
import { BackButton, hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import { isSuperUser } from '../../sc-utilities';
import Toolbar from '../../sc-utilities/Toolbar'
import * as actions from './store/systemCertificateActions';
import * as action from '../../np-core/Auth/store/authActions';
import SystemCertificateDelete from './SystemCertificateDelete';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginTop: 9,
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
const windowHeight = 195;

const SystemCertificateList = props => {
    const { classes, translations, deleting, onSystemCertificateCount, onSystemCertificateList, saveColumnsFilter, activeCustomerID, tableColumns, onBulkDeleteSystemCertificates, permissions, redirectHandler, tableRef } = props;

    const columnObject = useMemo(() => tableColumns, [tableColumns]);
    const [deleteModalState, setDeleteModalState] = useState({ open: false, SystemCertificateIDs: [] });

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['system_certificate_delete']);
    const deletePermission = hasPermission(permissions, 'system_certificate_delete');

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = false;
            if (!isSuperUser()) {
                if (!deletePermission || (deletePermission && !rowData.CustomerID)) {
                    disabled = true;
                }
            }
            rowData.tableData.disabled = disabled
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        filterCellStyle: { maxWidth: '200px' },
        minBodyHeight: window.innerHeight - windowHeight < 300 ? 300 : window.innerHeight - windowHeight,
        maxBodyHeight: window.innerHeight - windowHeight,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 20,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query?.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;

        let count = await onSystemCertificateCount(query);
        if (!query['page'] && !rowsPageSizeInit.includes(query['pageSize'])) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize;
            rowsPageSizeInit = dimensions.rowsPageSize;
            pageSize = dimensions.pageSize;
        }

        let result = count ? await onSystemCertificateList(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return (

            <div className={classes.customToolbar}>
                <div className={classes.tableTitle}>
                    {BackButton(translations, classes.customTooltip)}
                    <Typography className={classes.typography}>{translations["system_certificate"] || 'System certificate'}</Typography>
                    {hasPermission(permissions, 'system_certificate_create') ? <Fab
                        variant="extended"
                        size="small"
                        color="primary"
                        aria-label="Add"
                        className={classes.fab}
                        style={{ borderRadius: '1px' }}
                        onClick={() => redirectHandler(`/admin/system-certificate/create`)}
                    >
                        <Add size="small" />
                        {translations["create_new"] || "Create new"}
                    </Fab> : null}
                    <Tooltip title={translations["refresh"] || "Refresh"}>
                        <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
        )
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`SystemCertificate`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, systemCertificateIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteSystemCertificates({ SystemCertificateIDs: deleteModalState.systemCertificateIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, systemCertificateIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = systemCertificateIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, systemCertificateIDs: systemCertificateIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["system_certificate"] || 'System Certificate'}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(systemCertificate => systemCertificate.SystemCertificateID))
        },
    ];

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'system_certificate_delete') ? (
                <SystemCertificateDelete
                    entityName={translations["system_certificate"] || 'System Certificate'}
                    translations={translations}
                    deleting={deleting}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID || null,
        translations: state?.translate?.translations?.activeTranslations || {},
        deleting: state?.admin?.systemCertificate?.deleting,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onSystemCertificateCount: filter => dispatch(actions.fetchSystemCertificateCount(filter)),
        onSystemCertificateList: filter => dispatch(actions.fetchSystemCertificate(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteSystemCertificates: data => dispatch(actions.bulkDeleteSystemCertificates(data))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(SystemCertificateList));


